import React, { useEffect, useState } from 'react';
import { renderToString } from 'react-dom/server';
import {
  ProductHeaderBannerButtonsWrapper,
  ProductHeaderBannerContent,
  ProductHeaderBannerDescription,
  ProductHeaderBannerHeading,
  ProductHeaderBannerImage,
  ProductHeaderBannerTagline,
  ProductHeaderBannerWrapper,
  TopWrapper,
} from './ProductHeaderBanner.styles';
import { Button } from '@uk-source-web/button';
import { Heading } from '@uk-source-web/heading';
import { ProductHeaderBannerProps } from './ProductHeaderBanner.types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import usingOpcoUrl from '@/helpers/prefixHelper';
import { getImage, getSrc } from 'gatsby-plugin-image';
import { Paragraph } from '@legacy-vfuk/core-paragraph';
import { ShowStickyBanner } from './ProductHeaderBannerScroll/ProductHeaderBannerScroll.styles';
import ProductHeaderBannerScroll from './ProductHeaderBannerScroll/ProductHeaderBannerScroll';

const ProductHeaderBanner = ({
  heading,
  headingLevel,
  tagLine,
  bodyText,
  primaryButton,
  secondaryButton,
  backgroundImage,
  contentAlignment,
  productHeaderBannerScrollInfo,
}: ProductHeaderBannerProps) => {
  const [showScrollBanner, setShowScrollBanner] = useState(false);
  const [topOffset, setTopOffset] = useState<number>(0);
  const richTextBodyJson = bodyText && JSON.parse(bodyText.raw);
  const richTextBodyContent = richTextBodyJson && { ...richTextBodyJson, content: richTextBodyJson.content[0].content };

  const imageTitleSrc = getSrc(backgroundImage?.imageTitle);
  const imageTitlePlaceholder = getImage(backgroundImage?.imageTitle)?.placeholder?.fallback;
  const mobileBackgroundImageSrc = getSrc(backgroundImage?.mobileBackgroundImage);
  const mobileBackgroundImagePlaceholder = getImage(backgroundImage?.mobileBackgroundImage)?.placeholder?.fallback;
  const OPCO = process.env.GATSBY_OPCO;

  useEffect(() => {
    const handleScroll = () => {
      // When user scrolls down > 200px, show banner
      if (window.scrollY > 200) {
        setShowScrollBanner(true);
      } else {
        setShowScrollBanner(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const computeOffset = () => {
      const navEl = document.getElementById('sw-top-navigation');
      setTopOffset(navEl?.offsetHeight ?? 0);
    };

    computeOffset();
    window.addEventListener('resize', computeOffset);
    window.addEventListener('scroll', computeOffset);
    return () => {
      window.removeEventListener('resize', computeOffset);
      window.removeEventListener('scroll', computeOffset);
    };
  }, []);

  useEffect(() => {
    if (!productHeaderBannerScrollInfo) return;
    const className = 'has-phb-scroll';
    if (showScrollBanner) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }
    return () => document.body.classList.remove(className);
  }, [showScrollBanner, productHeaderBannerScrollInfo]);

  return (
    <TopWrapper>
      <ProductHeaderBannerWrapper>
        <ProductHeaderBannerContent contentAlignment={contentAlignment}>
          <ProductHeaderBannerHeading>
            <Heading level={headingLevel ? headingLevel : 4} text={heading} />
          </ProductHeaderBannerHeading>
          <ProductHeaderBannerTagline>
            <Paragraph weight={2}>{tagLine}</Paragraph>
          </ProductHeaderBannerTagline>{' '}
          <ProductHeaderBannerDescription contentAlignment={contentAlignment}>
            <Paragraph weight={2}>
              {bodyText && renderToString(documentToReactComponents(richTextBodyContent))}
            </Paragraph>
          </ProductHeaderBannerDescription>
          <ProductHeaderBannerButtonsWrapper>
            {primaryButton && (
              <Button
                text={primaryButton.label}
                href={usingOpcoUrl(primaryButton.url)}
                {...(primaryButton.customAttributesJson && {
                  htmlAttributes: { ...primaryButton.customAttributesJson },
                })}
              />
            )}
            {secondaryButton && (
              <Button
                text={secondaryButton.label}
                href={usingOpcoUrl(secondaryButton.url)}
                {...(secondaryButton.customAttributesJson && {
                  htmlAttributes: { ...secondaryButton.customAttributesJson },
                })}
              />
            )}
          </ProductHeaderBannerButtonsWrapper>
        </ProductHeaderBannerContent>
        <ProductHeaderBannerImage
          image={imageTitleSrc || imageTitlePlaceholder || ''}
          mobileImage={mobileBackgroundImageSrc || mobileBackgroundImagePlaceholder || ''}
        />
      </ProductHeaderBannerWrapper>
      {productHeaderBannerScrollInfo && (
        <ShowStickyBanner sticky={showScrollBanner} offsetTop={topOffset} OPCO={OPCO}>
          <ProductHeaderBannerScroll {...productHeaderBannerScrollInfo} />
        </ShowStickyBanner>
      )}
    </TopWrapper>
  );
};

export default ProductHeaderBanner;
